// Dev-only ledger of backend API calls. Mirrors the Helius ledger but
// for the Morfi backend SDK / any HTTP request matching the configured
// API URL prefix(es).
//
// === What it captures ===
//
// Every `fetch()` call to a known API origin is recorded with:
//   - HTTP method (GET / POST / etc.)
//   - Full path + parsed query string (with the SDK's `fields=` param
//     surfaced separately for visibility)
//   - Request body size, response size (from `Content-Length` when
//     present), and latency (ms)
//   - HTTP status code, ok flag
//   - JS-land call site walked from `new Error().stack`
//   - Duplicate burst flag when the same (method, path, query) repeats
//     within ~1s — usually a missing cache or a leaked re-render.
//
// === Plumbing ===
//
// `installApiInstrumentation()` (sibling file) replaces `globalThis.fetch`
// with a wrapper that filters by URL prefix and forwards records here.
// The wrapper proxies to native fetch unchanged for non-API URLs.
//
// === Zero-overhead in production ===
//
// Same pattern as `helius-registry`:
//   - Every public function gates on `IS_DEV` and returns a constant
//     no-op before touching state.
//   - State (the ring buffer, dedup map, listener set) is lazily
//     allocated on first dev-mode use via `ensureState()`.
//   - The instrumenter is installed only inside a
//     `process.env.NODE_ENV !== 'production'` ternary at the call site,
//     so the install + import is dead-code-eliminated and tree-shaken
//     out of production bundles.

const IS_DEV: boolean = process.env.NODE_ENV !== 'production';

const MAX_BUFFER_SIZE = 500;
const WINDOW_MS = 1000;
/** Cap captured request/response body strings to avoid unbounded heap
 *  growth. ~50KB per body × 500 records × 2 bodies ≈ 50MB worst case. */
export const MAX_BODY_BYTES = 50_000;

/** Truncate a captured body to {@link MAX_BODY_BYTES}, appending a
 *  marker so the engineer can tell it was clipped. */
export function truncateBody(s: string | null): string | null {
  if (s === null) return null;
  if (s.length <= MAX_BODY_BYTES) return s;
  return `${s.slice(0, MAX_BODY_BYTES)}\n…[truncated, ${String(s.length - MAX_BODY_BYTES)}B more]`;
}

/** Distinguishes the four classes of records the ledger holds:
 *  - `api`     — fetch matched a configured API origin (non-image response)
 *  - `image`   — image asset load (recorded via either the fetch wrapper
 *                when the response `Content-Type` is `image/*`, or via
 *                the PerformanceObserver path that captures `<img>`-
 *                initiated loads — `next/image`, picker previews, etc.).
 *                Surfaced as its own kind so engineers can filter the
 *                ledger to "just image traffic" and copy it independently.
 *  - `other`   — fetch went elsewhere (Helius RPC, telemetry, etc.)
 *  - `system`  — synthetic record from the instrumenter (install marker,
 *                config warning). Always pinned at the bottom of any
 *                "newest first" ordering by virtue of being inserted at
 *                install time.
 */
export type ApiCallKind = 'api' | 'image' | 'other' | 'system';

export interface ApiCall {
  readonly id: number;
  readonly timestamp: number;
  readonly kind: ApiCallKind;
  readonly method: string;
  readonly origin: string;
  readonly path: string;
  /** Parsed query as a stable `key=value&...` string for display + dedup. */
  readonly query: string;
  /** SDK-specific: comma-joined `fields=` param for quick scanning. Empty
   *  string when absent. */
  readonly fields: string;
  readonly requestBytes: number;
  /** From the response's `Content-Length` header; null if missing. */
  readonly responseBytes: number | null;
  readonly status: number;
  readonly ok: boolean;
  readonly latencyMs: number;
  readonly callSite: string | null;
  /** Burst size when this call duplicates a recent (method, path, query)
   *  within `WINDOW_MS`. > 1 = duplicated. */
  readonly burstSize: number;
  readonly burstId: number | null;
  /** Optional message — used by `system` records (e.g. "instrumentation
   *  installed", "no API origin configured"). */
  readonly message: string | null;
  /** Full request body (capped at MAX_BODY_BYTES). Null when the body
   *  was non-string (FormData / Blob / ArrayBuffer) or absent. */
  readonly requestBody: string | null;
  /** Full response body (capped at MAX_BODY_BYTES). Null when reading
   *  failed or content was non-text. */
  readonly responseBody: string | null;
}

export interface ApiSnapshot {
  /** Counts only `kind === 'api'` records. */
  readonly totalCalls: number;
  /** Counts every record regardless of kind, including `other` + `system`. */
  readonly totalAllRecords: number;
  readonly totalRequestBytes: number;
  readonly totalResponseBytes: number;
  readonly errorCalls: number;
  readonly duplicateCalls: number;
  readonly startedAt: number;
  readonly calls: readonly ApiCall[];
}

interface MutableApiCall {
  id: number;
  timestamp: number;
  kind: ApiCallKind;
  method: string;
  origin: string;
  path: string;
  query: string;
  fields: string;
  requestBytes: number;
  responseBytes: number | null;
  status: number;
  ok: boolean;
  latencyMs: number;
  callSite: string | null;
  burstSize: number;
  burstId: number | null;
  message: string | null;
  /** Internal — used for retro-update walk-back to keep POST/PUT/PATCH
   *  bursts accurate when path+query are identical but bodies differ. */
  bodyDigest: string;
  requestBody: string | null;
  responseBody: string | null;
}

interface RegistryState {
  buffer: MutableApiCall[];
  recent: Map<string, { timestamps: number[]; burstId: number }>;
  listeners: Set<() => void>;
  startedAt: number;
  totalCalls: number;
  totalRequestBytes: number;
  totalResponseBytes: number;
  idSeq: number;
  burstSeq: number;
  cachedSnapshot: ApiSnapshot;
}

interface ApiGlobals {
  __morfiApiLedger?: RegistryState;
}

const NOOP_SNAPSHOT: ApiSnapshot = Object.freeze({
  totalCalls: 0,
  totalAllRecords: 0,
  totalRequestBytes: 0,
  totalResponseBytes: 0,
  errorCalls: 0,
  duplicateCalls: 0,
  startedAt: 0,
  calls: [],
});

const NOOP_UNSUBSCRIBE = (): void => {};

let _state: RegistryState | null = null;

function ensureState(): RegistryState {
  if (_state !== null) return _state;
  const g = globalThis as typeof globalThis & ApiGlobals;
  const existing = g.__morfiApiLedger;
  if (existing !== undefined) {
    _state = existing;
    return existing;
  }
  const fresh: RegistryState = {
    buffer: [],
    recent: new Map(),
    listeners: new Set(),
    startedAt: Date.now(),
    totalCalls: 0,
    totalRequestBytes: 0,
    totalResponseBytes: 0,
    idSeq: 0,
    burstSeq: 0,
    cachedSnapshot: NOOP_SNAPSHOT,
  };
  fresh.cachedSnapshot = freezeSnapshotFrom(fresh);
  g.__morfiApiLedger = fresh;
  _state = fresh;
  return fresh;
}

function freezeSnapshotFrom(s: RegistryState): ApiSnapshot {
  let dupes = 0;
  let errors = 0;
  let apiCount = 0;
  const calls: ApiCall[] = s.buffer.map((c) => {
    if (c.kind === 'api') {
      apiCount += 1;
      if (c.burstSize > 1) dupes += 1;
      if (!c.ok) errors += 1;
    }
    return {
      id: c.id,
      timestamp: c.timestamp,
      kind: c.kind,
      method: c.method,
      origin: c.origin,
      path: c.path,
      query: c.query,
      fields: c.fields,
      requestBytes: c.requestBytes,
      responseBytes: c.responseBytes,
      status: c.status,
      ok: c.ok,
      latencyMs: c.latencyMs,
      callSite: c.callSite,
      burstSize: c.burstSize,
      burstId: c.burstId,
      message: c.message,
      requestBody: c.requestBody,
      responseBody: c.responseBody,
    };
  });
  return {
    totalCalls: apiCount,
    totalAllRecords: calls.length,
    totalRequestBytes: s.totalRequestBytes,
    totalResponseBytes: s.totalResponseBytes,
    errorCalls: errors,
    duplicateCalls: dupes,
    startedAt: s.startedAt,
    calls,
  };
}

function notify(s: RegistryState): void {
  for (const l of s.listeners) {
    l();
  }
}

export interface RecordApiCallArgs {
  readonly kind: ApiCallKind;
  readonly method: string;
  readonly origin: string;
  readonly path: string;
  readonly query: string;
  readonly fields: string;
  readonly requestBytes: number;
  readonly responseBytes: number | null;
  readonly status: number;
  readonly ok: boolean;
  readonly latencyMs: number;
  readonly callSite: string | null;
  readonly message?: string | null;
  /** Cheap fingerprint of the request body. Used by the dedup detector
   *  for non-idempotent methods (POST/PUT/PATCH) so two batch calls
   *  with different inner payloads don't false-flag as duplicates. */
  readonly bodyDigest?: string;
  readonly requestBody?: string | null;
  readonly responseBody?: string | null;
}

export function recordApiCall(args: RecordApiCallArgs): void {
  if (!IS_DEV) return;
  const s = ensureState();
  const now = Date.now();
  const cutoff = now - WINDOW_MS;

  const dupKey = `${args.method} ${args.path}?${args.query}#${args.bodyDigest ?? ''}`;
  const dupExisting = s.recent.get(dupKey);
  let burstId: number | null = null;
  let burstSize = 1;
  if (dupExisting !== undefined) {
    const fresh = dupExisting.timestamps.filter((t): boolean => t >= cutoff);
    if (fresh.length > 0) {
      burstId = dupExisting.burstId;
      burstSize = fresh.length + 1;
      // Retro-update older members of the burst — match the same key
      // shape (method + path + query + body digest) used to insert the
      // first call, so POSTs with distinct bodies don't false-merge.
      const newDigest = args.bodyDigest ?? '';
      for (let i = s.buffer.length - 1; i >= 0; i -= 1) {
        const c = s.buffer[i];
        if (c === undefined) continue;
        if (c.timestamp < cutoff) break;
        if (
          c.method === args.method &&
          c.path === args.path &&
          c.query === args.query &&
          c.bodyDigest === newDigest
        ) {
          c.burstId = burstId;
          c.burstSize = burstSize;
        }
      }
    }
    fresh.push(now);
    s.recent.set(dupKey, { timestamps: fresh, burstId: dupExisting.burstId });
  } else {
    s.burstSeq += 1;
    s.recent.set(dupKey, { timestamps: [now], burstId: s.burstSeq });
  }

  s.idSeq += 1;
  s.totalCalls += 1;
  s.totalRequestBytes += args.requestBytes;
  if (args.responseBytes !== null) {
    s.totalResponseBytes += args.responseBytes;
  }
  const record: MutableApiCall = {
    id: s.idSeq,
    timestamp: now,
    kind: args.kind,
    method: args.method,
    origin: args.origin,
    path: args.path,
    query: args.query,
    fields: args.fields,
    requestBytes: args.requestBytes,
    responseBytes: args.responseBytes,
    status: args.status,
    ok: args.ok,
    latencyMs: args.latencyMs,
    callSite: args.callSite,
    burstSize,
    burstId: burstSize > 1 ? burstId : null,
    message: args.message ?? null,
    bodyDigest: args.bodyDigest ?? '',
    requestBody: truncateBody(args.requestBody ?? null),
    responseBody: truncateBody(args.responseBody ?? null),
  };
  s.buffer.push(record);
  if (s.buffer.length > MAX_BUFFER_SIZE) {
    s.buffer.shift();
  }

  // Periodic GC of the dedup map.
  if (s.idSeq % 50 === 0) {
    for (const [k, v] of s.recent) {
      const newest = v.timestamps[v.timestamps.length - 1];
      if (newest === undefined || newest < cutoff) {
        s.recent.delete(k);
      }
    }
  }

  s.cachedSnapshot = freezeSnapshotFrom(s);
  notify(s);
}

/**
 * Insert a synthetic `system` record visible to the engineer in the
 * panel — used for the install marker and config warnings. Lets us
 * confirm the instrumenter actually ran without any console output.
 */
export function recordSystemMessage(message: string): void {
  if (!IS_DEV) return;
  const s = ensureState();
  s.idSeq += 1;
  const record: MutableApiCall = {
    id: s.idSeq,
    timestamp: Date.now(),
    kind: 'system',
    method: '',
    origin: '',
    path: '',
    query: '',
    fields: '',
    requestBytes: 0,
    responseBytes: null,
    status: 0,
    ok: true,
    latencyMs: 0,
    callSite: null,
    burstSize: 1,
    burstId: null,
    message,
    bodyDigest: '',
    requestBody: null,
    responseBody: null,
  };
  s.buffer.push(record);
  if (s.buffer.length > MAX_BUFFER_SIZE) {
    s.buffer.shift();
  }
  s.cachedSnapshot = freezeSnapshotFrom(s);
  notify(s);
}

export function subscribeApiRegistry(listener: () => void): () => void {
  if (!IS_DEV) return NOOP_UNSUBSCRIBE;
  const s = ensureState();
  s.listeners.add(listener);
  return (): void => {
    s.listeners.delete(listener);
  };
}

export function getApiSnapshot(): ApiSnapshot {
  if (!IS_DEV) return NOOP_SNAPSHOT;
  return ensureState().cachedSnapshot;
}

export function clearApiStats(): void {
  if (!IS_DEV) return;
  const s = ensureState();
  s.buffer = [];
  s.recent.clear();
  s.totalCalls = 0;
  s.totalRequestBytes = 0;
  s.totalResponseBytes = 0;
  s.startedAt = Date.now();
  s.cachedSnapshot = freezeSnapshotFrom(s);
  notify(s);
}

export function captureApiCallSite(): string | null {
  if (!IS_DEV) return null;
  const err = new Error();
  const stack = err.stack;
  if (typeof stack !== 'string') return null;
  const lines = stack.split('\n');
  for (const raw of lines) {
    const line = raw.trim();
    if (line.length === 0) continue;
    if (line.includes('api-registry')) continue;
    if (line.includes('api-instrument')) continue;
    if (line.includes('node_modules')) continue;
    if (line.includes('/web3.js/')) continue;

    const match = /\/src\/([^):\s]+:\d+(?::\d+)?)/.exec(line);
    if (match !== null && match[1] !== undefined) {
      return match[1].replace(/:\d+$/, '');
    }
  }
  return null;
}

export function formatApiSnapshot(
  snapshot: ApiSnapshot,
  now: number,
  callsOverride?: readonly ApiCall[],
  filterLabel?: string,
): string {
  const lines: string[] = [];
  const elapsedS = Math.max(1, Math.floor((now - snapshot.startedAt) / 1000));
  const ratePerMin = ((snapshot.totalCalls / elapsedS) * 60).toFixed(1);
  // When a filter override is supplied, format ONLY the filtered calls
  // and label the report accordingly so the reader knows the scope.
  const calls = callsOverride ?? snapshot.calls;
  const heading =
    filterLabel !== undefined && filterLabel.length > 0
      ? `# API call ledger — filtered: ${filterLabel}`
      : '# API call ledger';
  lines.push(heading);
  lines.push(`captured: ${new Date(now).toISOString()}`);
  if (callsOverride === undefined) {
    lines.push(
      `total: ${String(snapshot.totalCalls)} calls (${String(snapshot.errorCalls)} errors)` +
        ` over ${String(elapsedS)}s (~${ratePerMin}/min)` +
        ` — duplicates: ${String(snapshot.duplicateCalls)}`,
    );
    lines.push(
      `bytes: ${formatBytes(snapshot.totalRequestBytes)} sent / ${formatBytes(snapshot.totalResponseBytes)} received`,
    );
  } else {
    let bytes = 0;
    let errors = 0;
    let dupes = 0;
    for (const c of calls) {
      if (c.responseBytes !== null) bytes += c.responseBytes;
      if (c.kind !== 'system' && !c.ok) errors += 1;
      if (c.burstSize > 1) dupes += 1;
    }
    lines.push(
      `filtered: ${String(calls.length)} records (${String(errors)} errors, ${String(dupes)} dupes)` +
        ` — ${formatBytes(bytes)} received`,
    );
    lines.push(
      `window: ${String(elapsedS)}s — full ledger has ${String(snapshot.totalCalls)} API calls (~${ratePerMin}/min)`,
    );
  }
  lines.push('');
  // The `calls` argument is already in chronological order; iterate
  // from the back so the markdown report matches the panel's
  // "newest first" rendering.
  lines.push('## Calls (newest first)');
  for (let i = calls.length - 1; i >= 0; i -= 1) {
    const c = calls[i];
    if (c === undefined) continue;
    const dup = c.burstSize > 1 ? ` ⚠ DUPE×${String(c.burstSize)}` : '';
    const err = !c.ok ? ` ❌ ${String(c.status)}` : ` ${String(c.status)}`;
    const fieldsHint = c.fields.length > 0 ? ` fields=${c.fields}` : '';
    const site = c.callSite !== null ? ` @ ${c.callSite}` : '';
    const respSize = c.responseBytes !== null ? formatBytes(c.responseBytes) : '?';
    const kindTag = c.kind === 'image' ? ' 🖼' : '';
    lines.push(
      `- \`${formatTime(c.timestamp)}\` **${c.method}** ${c.path}${c.query.length > 0 ? `?${c.query}` : ''}` +
        `${kindTag}${err} ${respSize} ${c.latencyMs.toFixed(0)}ms${fieldsHint}${site}${dup}`,
    );
  }
  return lines.join('\n');
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  const ms = String(d.getMilliseconds()).padStart(3, '0');
  return `${hh}:${mm}:${ss}.${ms}`;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${String(n)}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}KB`;
  return `${(n / (1024 * 1024)).toFixed(2)}MB`;
}

/** Test-only — wipes registry state. Not exported from the public barrel. */
export function __resetApiRegistryForTests(): void {
  _state = null;
  const g = globalThis as typeof globalThis & ApiGlobals;
  delete g.__morfiApiLedger;
}
